import React from 'react';
import Logger from '../utils/intelligentLogger';

// Page cache interface
interface PageCache {
  component: React.ComponentType<any>;
  data?: any;
  timestamp: number;
  priority: number;
  accessCount: number;
}

// Preloading priority configuration
const PRELOAD_PRIORITY = {
  '/profile': 10,     // High priority - user profile
  '/chat': 9,         // High priority - messaging
  '/discover': 8,     // High priority - main feature
  '/network': 6,      // Medium priority
  '/settings': 4,     // Lower priority
  '/chat/': 7,        // Chat conversations
  '/profile/photo': 3, // Photo upload
} as const;

// Cache configuration
const CACHE_CONFIG = {
  MAX_SIZE: 20,           // Maximum cached pages
  EXPIRY_TIME: 10 * 60 * 1000, // 10 minutes
  PRELOAD_DELAY: 1000,    // 1 second delay before starting preload
  BATCH_SIZE: 2,          // Preload 2 pages at a time
  BATCH_DELAY: 500,       // 500ms between batches
} as const;

class PreloadingService {
  private preloadedPages: Map<string, PageCache> = new Map();
  private preloadQueue: string[] = [];
  private isPreloading: boolean = false;
  private preloadTimeout: NodeJS.Timeout | null = null;
  private performanceMonitor: PerformanceMonitor;

  constructor() {
    this.performanceMonitor = new PerformanceMonitor();
    this.initializePreloadQueue();
  }

  private initializePreloadQueue() {
    // Initialize queue with priority order
    this.preloadQueue = Object.keys(PRELOAD_PRIORITY).sort((a, b) => 
      PRELOAD_PRIORITY[b as keyof typeof PRELOAD_PRIORITY] - PRELOAD_PRIORITY[a as keyof typeof PRELOAD_PRIORITY]
    );
  }

  /**
   * Start background preloading after current page is ready
   */
  async startBackgroundPreloading(currentPath?: string) {
    if (this.isPreloading) {
      Logger.throttle('preload-in-progress', '🔄 Preloading already in progress');
      return;
    }

    // Clear any existing timeout
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
    }

    // Wait for current page to be fully loaded
    this.preloadTimeout = setTimeout(async () => {
      Logger.preload('Starting background preloading...');
      this.isPreloading = true;
      await this.preloadPages(currentPath);
    }, CACHE_CONFIG.PRELOAD_DELAY);
  }

  /**
   * Preload pages in batches with priority
   */
  private async preloadPages(currentPath?: string) {
    try {
      // Filter out current page and already cached pages
      const pagesToPreload = this.preloadQueue.filter(path => {
        if (currentPath && path === currentPath) return false;
        if (this.preloadedPages.has(path)) return false;
        return true;
      });

      // Only log if there are actually pages to preload
      if (pagesToPreload.length > 0) {
        Logger.preload(`Preloading ${pagesToPreload.length} pages in background`);
      }

      // Preload in batches
      for (let i = 0; i < pagesToPreload.length; i += CACHE_CONFIG.BATCH_SIZE) {
        const batch = pagesToPreload.slice(i, i + CACHE_CONFIG.BATCH_SIZE);
        
        await Promise.allSettled(
          batch.map(path => this.preloadSinglePage(path))
        );

        // Wait between batches to avoid overwhelming the system
        if (i + CACHE_CONFIG.BATCH_SIZE < pagesToPreload.length) {
          await new Promise(resolve => setTimeout(resolve, CACHE_CONFIG.BATCH_DELAY));
        }
      }

      if (pagesToPreload.length > 0) {
        Logger.preload('Background preloading completed');
      }
    } catch (error) {
      Logger.error('Background preloading failed:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Preload a single page
   */
  private async preloadSinglePage(path: string): Promise<void> {
    try {
      const startTime = Date.now();
      const priority = PRELOAD_PRIORITY[path as keyof typeof PRELOAD_PRIORITY] || 1;

      // Dynamically import the page component
      const component = await this.loadPageComponent(path);
      
      // Preload initial data if needed
      const data = await this.preloadPageData(path);

      // Cache the page
      this.setPageCache(path, component, data, priority);

      const loadTime = Date.now() - startTime;
      Logger.throttle(`preload-${path}`, `Preloaded: ${path} (Priority: ${priority}, Time: ${loadTime}ms)`);
      
      // Track performance metrics
      Logger.performance.trackPreload(path, loadTime);
      this.performanceMonitor.trackPreload(path, loadTime);
    } catch (error) {
      Logger.debug(`Preload failed: ${path}`, error);
    }
  }

  /**
   * Load page component dynamically
   */
  private async loadPageComponent(path: string): Promise<React.ComponentType<any>> {
    switch (path) {
      case '/profile':
        const { default: Profile } = await import('../pages/Profile');
        return Profile;
      case '/chat':
        const { default: ChatList } = await import('../pages/ChatList');
        return ChatList;
      case '/discover':
        const { default: Discover } = await import('../pages/Discover');
        return Discover;
      case '/network':
        const { default: Network } = await import('../pages/Network');
        return Network;
      case '/settings':
        const { default: Settings } = await import('../pages/Settings');
        return Settings;
      case '/profile/photo':
        const { default: PhotoUploadPage } = await import('../pages/PhotoUploadPage');
        return PhotoUploadPage;
      default:
        // Handle dynamic routes like chat conversations
        if (path.startsWith('/chat/')) {
          const { default: ChatConversation } = await import('../pages/ChatConversation');
          return ChatConversation;
        }
        throw new Error(`Unknown page path: ${path}`);
    }
  }

  /**
   * Preload page data if needed
   */
  private async preloadPageData(path: string): Promise<any> {
    // For now, return null - data can be loaded on demand
    // This can be extended to preload common data for each page
    return null;
  }

  /**
   * Get preloaded page from cache
   */
  getPreloadedPage(path: string): React.ComponentType<any> | null {
    const cached = this.preloadedPages.get(path);
    
    if (!cached) {
      return null;
    }

    // Check if cache has expired
    if (Date.now() - cached.timestamp > CACHE_CONFIG.EXPIRY_TIME) {
      this.preloadedPages.delete(path);
      return null;
    }

    // Update access count and timestamp for LRU
    cached.accessCount++;
    cached.timestamp = Date.now();
    
    this.performanceMonitor.trackCacheHit(path);
    return cached.component;
  }

  /**
   * Set page in cache with LRU management
   */
  private setPageCache(path: string, component: React.ComponentType<any>, data: any, priority: number) {
    // Implement LRU eviction if cache is full
    if (this.preloadedPages.size >= CACHE_CONFIG.MAX_SIZE) {
      this.evictLeastRecentlyUsed();
    }

    this.preloadedPages.set(path, {
      component,
      data,
      timestamp: Date.now(),
      priority,
      accessCount: 0
    });
  }

  /**
   * Evict least recently used page from cache
   */
  private evictLeastRecentlyUsed() {
    let oldestPath = '';
    let oldestTime = Date.now();
    let lowestPriority = Infinity;

    for (const [path, cache] of this.preloadedPages.entries()) {
      // Consider both access time and priority
      const score = cache.timestamp + (cache.priority * 1000);
      if (score < oldestTime + (lowestPriority * 1000)) {
        oldestPath = path;
        oldestTime = cache.timestamp;
        lowestPriority = cache.priority;
      }
    }

    if (oldestPath) {
      Logger.debug(`Evicting from cache: ${oldestPath}`);
      this.preloadedPages.delete(oldestPath);
    }
  }

  /**
   * Invalidate specific page or entire cache
   */
  invalidate(path?: string) {
    if (path) {
      this.preloadedPages.delete(path);
      Logger.debug(`Invalidated cache for: ${path}`);
    } else {
      this.preloadedPages.clear();
      Logger.debug('Cleared entire preload cache');
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.preloadedPages.size,
      maxSize: CACHE_CONFIG.MAX_SIZE,
      isPreloading: this.isPreloading,
      cachedPages: Array.from(this.preloadedPages.keys()),
      performance: this.performanceMonitor.getStats()
    };
  }

  /**
   * Track navigation performance
   */
  trackNavigation(path: string, startTime: number) {
    this.performanceMonitor.trackNavigation(path, startTime);
  }

  /**
   * Stop preloading
   */
  stopPreloading() {
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
      this.preloadTimeout = null;
    }
    this.isPreloading = false;
    Logger.debug('Preloading stopped');
  }
}

/**
 * Performance monitoring for preloading
 */
class PerformanceMonitor {
  private preloadTimes: Map<string, number> = new Map();
  private cacheHits: Map<string, number> = new Map();
  private navigationTimes: Map<string, number> = new Map();

  trackPreload(path: string, loadTime: number) {
    this.preloadTimes.set(path, loadTime);
  }

  trackCacheHit(path: string) {
    const current = this.cacheHits.get(path) || 0;
    this.cacheHits.set(path, current + 1);
  }

  trackNavigation(path: string, startTime: number) {
    const loadTime = Date.now() - startTime;
    this.navigationTimes.set(path, loadTime);
  }

  getStats() {
    const avgPreloadTime = this.getAverageTime(this.preloadTimes);
    const avgNavigationTime = this.getAverageTime(this.navigationTimes);
    const totalCacheHits = Array.from(this.cacheHits.values()).reduce((a, b) => a + b, 0);

    return {
      avgPreloadTime,
      avgNavigationTime,
      totalCacheHits,
      cacheHitRate: this.calculateCacheHitRate()
    };
  }

  private getAverageTime(times: Map<string, number>): number {
    const values = Array.from(times.values());
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  }

  private calculateCacheHitRate(): number {
    const hits = Array.from(this.cacheHits.values()).reduce((a, b) => a + b, 0);
    const total = Array.from(this.navigationTimes.values()).length;
    return total > 0 ? (hits / total) * 100 : 0;
  }
}

// Create singleton instance
const preloadingService = new PreloadingService();

export default preloadingService;
export { PreloadingService, PerformanceMonitor }; 